import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Bot, Shield, Key, Filter } from 'lucide-react'

interface Agent {
  id: string
  tenant_id: string
  tenant_name: string
  agent_name: string
  agent_description: string | null
  type: string
  capabilities: string[]
  status: string
}

const TYPE_COLORS: Record<string, string> = {
  agent: 'bg-blue-100 text-blue-800',
  mcp_server: 'bg-purple-100 text-purple-800',
  service_account: 'bg-green-100 text-green-800',
  human: 'bg-gray-100 text-gray-800'
}

const TYPE_LABELS: Record<string, string> = {
  agent: 'AI Agent',
  mcp_server: 'MCP Server',
  service_account: 'Service Account',
  human: 'Human Operator'
}

const TIER_PRICING = [
  { tier: 'basic', label: 'Basic', price: 'Free', desc: 'Name, type, status' },
  { tier: 'capability', label: 'Capability', price: '$0.03/query', desc: '+ Capabilities & data access' },
  { tier: 'trust', label: 'Trust', price: '$0.15/query', desc: '+ Risk score & compliance' },
  { tier: 'deep_access', label: 'Deep Access', price: '$0.75/query', desc: 'Full audit evidence (NDA required)' }
]

export default function AgentRegistryPage() {
  const navigate = useNavigate()
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(false)
  const [q, setQ] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [capFilter, setCapFilter] = useState('')
  const [error, setError] = useState('')

  const search = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (q) params.set('q', q)
      if (typeFilter) params.set('type', typeFilter)
      if (capFilter) params.set('capability', capFilter)
      params.set('limit', '50')

      const res = await fetch(`/api/registry/search?${params}`)
      if (!res.ok) throw new Error('Search failed')
      const data = await res.json()
      setAgents(data.agents ?? [])
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [q, typeFilter, capFilter])

  useEffect(() => {
    const timer = setTimeout(search, 300)
    return () => clearTimeout(timer)
  }, [search])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-12 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex justify-center mb-4">
            <Bot className="h-12 w-12 text-blue-300" />
          </div>
          <h1 className="text-4xl font-bold mb-3">Agent Registry</h1>
          <p className="text-blue-200 text-lg max-w-2xl mx-auto">
            Discover and verify AI agents, MCP servers, and service accounts.
            Query trust profiles, capabilities, and compliance status.
          </p>
          <div className="flex gap-3 justify-center mt-6 flex-wrap">
            <a href="/admin" className="bg-white text-blue-900 px-5 py-2 rounded-lg font-medium text-sm hover:bg-blue-50">
              Register Your Agent
            </a>
            <a
              href="/admin"
              className="border border-blue-400 text-white px-5 py-2 rounded-lg font-medium text-sm hover:bg-blue-800"
            >
              Get API Key
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Search & Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search agents by name or description..."
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Types</option>
            <option value="agent">AI Agent</option>
            <option value="mcp_server">MCP Server</option>
            <option value="service_account">Service Account</option>
            <option value="human">Human Operator</option>
          </select>
          <input
            type="text"
            value={capFilter}
            onChange={e => setCapFilter(e.target.value)}
            placeholder="Filter by capability..."
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
        )}

        {/* Results */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Searching...</div>
        ) : agents.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Bot className="h-10 w-10 mx-auto mb-3 text-gray-300" />
            <p>No agents found. <a href="/admin" className="text-blue-600 hover:underline">Register yours</a>.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {agents.map(agent => (
              <div
                key={agent.id}
                className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md hover:border-blue-200 transition-all cursor-pointer"
                onClick={() => navigate(`/registry/${agent.id}`)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{agent.agent_name}</h3>
                    <p className="text-sm text-gray-500">{agent.tenant_name}</p>
                  </div>
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ml-2 ${TYPE_COLORS[agent.type] ?? 'bg-gray-100 text-gray-800'}`}>
                    {TYPE_LABELS[agent.type] ?? agent.type}
                  </span>
                </div>
                {agent.agent_description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{agent.agent_description}</p>
                )}
                {agent.capabilities.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {agent.capabilities.map(cap => (
                      <span key={cap} className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full">{cap}</span>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-1 text-xs text-green-600">
                    <Shield className="h-3 w-3" />
                    <span>Query available</span>
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); navigate(`/registry/${agent.id}`) }}
                    className="text-xs text-blue-600 hover:underline font-medium"
                  >
                    View Profile →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tier Pricing */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Key className="h-5 w-5 text-gray-400" />
            Query Tier Pricing
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {TIER_PRICING.map(t => (
              <div key={t.tier} className="border border-gray-200 rounded-lg p-4">
                <div className="font-medium text-gray-900 text-sm">{t.label}</div>
                <div className="text-blue-600 font-semibold mt-1">{t.price}</div>
                <div className="text-gray-500 text-xs mt-1">{t.desc}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Basic queries are always free. Get an API key at <a href="/admin" className="text-blue-600 hover:underline">/admin</a> for metered tiers.
          </p>
        </div>
      </div>
    </div>
  )
}
